import { useChat } from "@ai-sdk/react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { DefaultChatTransport, lastAssistantMessageIsCompleteWithApprovalResponses, type UIMessage } from "ai";
import { ArrowUp, Square } from "lucide-react";
import { type FormEvent, useEffect, useMemo, useRef, useState } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { formatDayHuman } from "@cafe/shared";
import { api, unwrap } from "../lib/api";
import { cn } from "../lib/utils";
import { isToolPart, toolNameOf, type ToolPartLike, unwrapOutput } from "../lib/tool-parts";
import { Trace } from "./Trace";
import { ApprovalContext, ToolPart } from "./tools/ToolPart";
import { Spinner } from "./ui/primitives";

const SUGGESTIONS = [
  "¿Cómo me fue en octubre comparado con septiembre?",
  "¿Cuál es mi peor día de la semana y qué harías al respecto?",
  "¿Qué sucursal me debería preocupar?",
  "¿Qué clientes estoy perdiendo y a cuántos puedo contactar?",
  "¿Qué campañas me han funcionado mejor? ¿Repetirías alguna?",
  "Prepara una campaña para recuperar clientes y mándala.",
];

export function Chat({ conversationId, initialMessages }: { conversationId: string; initialMessages: UIMessage[] }) {
  const qc = useQueryClient();
  const transport = useMemo(() => new DefaultChatTransport({ api: "/api/chat", credentials: "same-origin" }), []);
  const { messages, sendMessage, status, stop, error, clearError, addToolApprovalResponse } = useChat({
    id: conversationId,
    messages: initialMessages,
    transport,
    // After the owner approves/rejects a proposal, continue the agent turn automatically.
    sendAutomaticallyWhen: lastAssistantMessageIsCompleteWithApprovalResponses,
    onFinish: () => qc.invalidateQueries({ queryKey: ["conversations"] }),
  });
  const respond = useMemo(() => (id: string, approved: boolean) => void addToolApprovalResponse({ id, approved }), [addToolApprovalResponse]);
  const [input, setInput] = useState("");
  const bottom = useRef<HTMLDivElement>(null);
  const busy = status === "submitted" || status === "streaming";

  useEffect(() => {
    bottom.current?.scrollIntoView({ behavior: "smooth", block: "end" });
  }, [messages, status]);

  const submit = (text: string) => {
    const t = text.trim();
    if (!t || busy) return;
    clearError();
    sendMessage({ text: t });
    setInput("");
  };
  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    submit(input);
  };

  return (
    <div className="flex h-full min-h-0 flex-col">
      <div className="min-h-0 flex-1 overflow-y-auto px-3 py-4 sm:px-6">
        <div className="mx-auto flex max-w-3xl flex-col gap-4">
          {messages.length === 0 && <EmptyState onPick={submit} />}
          <ApprovalContext.Provider value={respond}>
            {messages.map((m) => (
              <MessageView key={m.id} message={m} />
            ))}
          </ApprovalContext.Provider>
          {status === "submitted" && (
            <p className="flex items-center gap-2 text-sm text-roast/80">
              <Spinner className="size-3.5" /> Pensando…
            </p>
          )}
          {error && (
            <p role="alert" className="rounded-xl bg-red-50 px-3 py-2 text-sm text-red-800">
              {friendlyError(error)}
            </p>
          )}
          <div ref={bottom} />
        </div>
      </div>
      <form onSubmit={onSubmit} className="border-t border-latte bg-foam px-3 py-3 pb-[max(0.75rem,env(safe-area-inset-bottom))] sm:px-6">
        <div className="mx-auto flex max-w-3xl items-end gap-2 rounded-2xl border border-latte bg-white px-3 py-2 focus-within:border-caramel focus-within:ring-2 focus-within:ring-caramel/20">
          <textarea
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                submit(input);
              }
            }}
            rows={1}
            placeholder="Pregúntame por tus ventas, clientes o pídeme una campaña…"
            aria-label="Mensaje"
            className="max-h-40 min-h-[2.25rem] flex-1 resize-none bg-transparent py-1.5 text-[15px] outline-none placeholder:text-roast/50"
          />
          {busy ? (
            <button type="button" onClick={() => stop()} className="grid size-9 place-items-center rounded-xl bg-latte text-espresso" aria-label="Detener">
              <Square className="size-4" />
            </button>
          ) : (
            <button type="submit" disabled={!input.trim()} className="grid size-9 place-items-center rounded-xl bg-espresso text-foam disabled:opacity-40" aria-label="Enviar">
              <ArrowUp className="size-4" />
            </button>
          )}
        </div>
      </form>
    </div>
  );
}

function friendlyError(error: Error): string {
  try {
    const parsed = JSON.parse(error.message) as { error?: { message?: string } };
    if (parsed.error?.message) return parsed.error.message;
  } catch {
    /* not JSON */
  }
  return error.message || "Algo salió mal. Intenta de nuevo.";
}

const DATA_CHIP_EXCLUDE = new Set(["show_chart", "show_table", "show_kpis", "create_campaign_draft"]);

function MessageView({ message }: { message: UIMessage }) {
  if (message.role === "user") {
    const text = message.parts.map((p) => (p.type === "text" ? p.text : "")).join("");
    return <div className="ml-auto max-w-[85%] rounded-2xl rounded-br-md bg-espresso px-3.5 py-2 text-[15px] text-foam">{text}</div>;
  }
  // Group consecutive "data used" chips on one line; visuals, cards and text get their own blocks.
  const blocks: Array<{ key: string; chips?: ToolPartLike[]; node?: React.ReactNode }> = [];
  message.parts.forEach((part, i) => {
    if (isToolPart(part)) {
      const p = part as unknown as ToolPartLike;
      const name = toolNameOf(p);
      const done = p.state === "output-available" && !unwrapOutput(p.output).isError;
      if (!DATA_CHIP_EXCLUDE.has(name) && done) {
        const last = blocks[blocks.length - 1];
        if (last?.chips) last.chips.push(p);
        else blocks.push({ key: `c${i}`, chips: [p] });
        return;
      }
      blocks.push({ key: `t${i}`, node: <ToolPart part={p} /> });
    } else if (part.type === "text" && part.text) {
      blocks.push({
        key: `x${i}`,
        node: (
          <div className="prose-chat text-[15px]">
            <ReactMarkdown remarkPlugins={[remarkGfm]}>{part.text}</ReactMarkdown>
          </div>
        ),
      });
    }
  });
  return (
    <div className="flex max-w-full flex-col gap-2">
      {blocks.map((b) =>
        b.chips ? (
          <div key={b.key} className="flex flex-wrap gap-1.5">
            {b.chips.map((c, j) => (
              <ToolPart key={j} part={c} />
            ))}
          </div>
        ) : (
          <div key={b.key}>{b.node}</div>
        ),
      )}
      <Trace message={message} />
    </div>
  );
}

function EmptyState({ onPick }: { onPick: (s: string) => void }) {
  const biz = useQuery({ queryKey: ["business"], queryFn: async () => unwrap(await api.insights.business.$get()), staleTime: Infinity });
  return (
    <div className="py-6 text-center sm:py-12">
      <h1 className="text-2xl font-semibold">¡Hola! ☕</h1>
      <p className="mt-1 text-roast/80">Pregúntame lo que quieras de tu negocio o pídeme ayuda con una campaña.</p>
      {biz.data && (
        <p className="mt-2 inline-flex rounded-full bg-latte/60 px-3 py-1 text-xs text-roast">
          Datos al {formatDayHuman(biz.data.business.data_as_of)} · {biz.data.locations.map((l) => l.name).join(", ")}
        </p>
      )}
      <div className="mt-6 grid gap-2 text-left sm:grid-cols-2">
        {SUGGESTIONS.map((s) => (
          <button key={s} onClick={() => onPick(s)} className={cn("rounded-xl border border-latte bg-foam px-3 py-2.5 text-sm transition hover:border-caramel hover:bg-white")}>
            {s}
          </button>
        ))}
      </div>
    </div>
  );
}
